/**
 * Digital Twin Assessment DTOs
 *
 * Zod schemas and TypeScript types for the loan assessment platform.
 * Service 18 (Digital Twin)
 */

import { z } from 'zod';
import {
  AssessmentCategory,
  AssessmentCategorySchema,
  AssessmentEventType,
  AssessmentEventTypeSchema,
  AssessmentSourceType,
  AssessmentSourceTypeSchema,
  AssessmentStatus,
  AssessmentStatusSchema,
  CreditBureau,
  CreditBureauSchema,
  FactorStatus,
  FactorStatusSchema,
  PropertyFactorName,
  PropertyRiskFlagSeverity,
  PropertyRunDepth,
  PropertyValuationSource,
  PropertyVerdict,
  RehabCategory,
  RiskBand,
  RiskBandSchema,
  TwinRole,
  TwinRoleSchema,
  TwinStatus,
  TwinStatusSchema,
  ValuationSource,
  ValuationSourceSchema,
} from '../enums/assessment.enums';

// Re-export enums for convenience
export {
  AssessmentCategory,
  AssessmentEventType,
  AssessmentSourceType,
  AssessmentStatus,
  CreditBureau,
  FactorStatus,
  PropertyFactorName,
  PropertyRiskFlagSeverity,
  PropertyRunDepth,
  PropertyValuationSource,
  PropertyVerdict,
  RehabCategory,
  RiskBand,
  TwinRole,
  TwinStatus,
  ValuationSource,
};

// ============================================================================
// ASSESSMENT RECOMMENDATION
// ============================================================================

/** Possible recommendation outcomes for a loan assessment */
export const AssessmentRecommendation = z.enum(['APPROVE', 'REJECT', 'REVIEW', 'PENDING']);
export type AssessmentRecommendationValue = z.infer<typeof AssessmentRecommendation>;

// ============================================================================
// APPLICATION STRENGTH
// ============================================================================

/** Strength classification for a loan application */
export const ApplicationStrengthLevel = z.enum(['STRONG', 'GOOD', 'NEEDS_ATTENTION', 'PENDING']);
export type ApplicationStrengthLevelValue = z.infer<typeof ApplicationStrengthLevel>;

// ============================================================================
// ASSESSMENT FACTOR DTO
// ============================================================================

/**
 * Individual factor contributing to an overall assessment score
 */
export const AssessmentFactorSchema = z.object({
  id: z.string().uuid(),
  twinRole: TwinRoleSchema,
  category: AssessmentCategorySchema,
  name: z.string(),
  value: z.string(),
  numericValue: z.number().nullable().optional(),
  score: z.number().min(0).max(100),
  weight: z.number().min(0).max(5),
  status: FactorStatusSchema,
  details: z.record(z.string(), z.unknown()).nullable().optional(),
  sourceType: AssessmentSourceTypeSchema,
  assessedAt: z.string().datetime(),
});
export type AssessmentFactorDto = z.infer<typeof AssessmentFactorSchema>;

// ============================================================================
// LOAN ASSESSMENT DTO
// ============================================================================

/**
 * Overall loan assessment aggregating all factor scores
 */
export const LoanAssessmentSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  businessId: z.string().uuid(),
  consumerId: z.string().uuid().nullable().optional(),
  overallScore: z.number().min(0).max(100),
  riskBand: RiskBandSchema,
  recommendation: AssessmentRecommendation,
  confidenceLevel: z.number().min(0).max(1),
  completenessScore: z.number().min(0).max(100),
  status: AssessmentStatusSchema,
  summary: z.string().nullable().optional(),
  lastAssessedAt: z.string().datetime(),
  factors: z.array(AssessmentFactorSchema).optional(),
});
export type LoanAssessmentDto = z.infer<typeof LoanAssessmentSchema>;

// ============================================================================
// CREDIT SNAPSHOT DTO
// ============================================================================

/**
 * Point-in-time credit bureau data snapshot
 */
export const CreditSnapshotSchema = z.object({
  id: z.string().uuid(),
  bureau: CreditBureauSchema,
  ficoScore: z.number().int().min(300).max(850),
  totalDebt: z.number().min(0),
  utilizationRate: z.number().min(0).max(1),
  derogatoryCount: z.number().int().min(0),
  inquiryCount12Mo: z.number().int().min(0),
  pulledAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});
export type CreditSnapshotDto = z.infer<typeof CreditSnapshotSchema>;

// ============================================================================
// PROPERTY VALUATION DTO
// ============================================================================

/**
 * Property valuation data from various sources
 */
export const PropertyValuationSchema = z.object({
  id: z.string().uuid(),
  propertyAddress: z.string(),
  propertyType: z.string(),
  purchasePrice: z.number().min(0),
  appraisedValue: z.number().min(0).nullable().optional(),
  arv: z.number().min(0).nullable().optional(),
  ltv: z.number().min(0).nullable().optional(),
  ltc: z.number().min(0).nullable().optional(),
  source: ValuationSourceSchema,
});
export type PropertyValuationDto = z.infer<typeof PropertyValuationSchema>;

// ============================================================================
// APPLICATION STRENGTH DTO
// ============================================================================

/**
 * Summary of application strength with actionable items
 */
export const ApplicationStrengthSchema = z.object({
  strength: ApplicationStrengthLevel,
  helpingFactors: z.array(z.string()),
  neededActions: z.array(z.string()),
  nextStep: z.string().nullable().optional(),
  completeness: z.number().min(0).max(100),
});
export type ApplicationStrengthDto = z.infer<typeof ApplicationStrengthSchema>;

// ============================================================================
// DIGITAL TWIN DTO
// ============================================================================

/**
 * Digital twin configuration and metadata
 */
export const DigitalTwinSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  role: TwinRoleSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  status: TwinStatusSchema,
  config: z.record(z.string(), z.unknown()).nullable().optional(),
  triggerEvents: z.array(z.string()).optional(),
  promptTemplateId: z.string().uuid().nullable().optional(),
  modelConfigId: z.string().uuid().nullable().optional(),
  workflowId: z.string().uuid().nullable().optional(),
});
export type DigitalTwinDto = z.infer<typeof DigitalTwinSchema>;

// ============================================================================
// ASSESSMENT EVENT DTO
// ============================================================================

/**
 * Event record for assessment lifecycle tracking
 */
export const AssessmentEventSchema = z.object({
  id: z.string().uuid(),
  assessmentId: z.string().uuid(),
  caseId: z.string().uuid(),
  twinId: z.string().uuid().nullable().optional(),
  twinRole: TwinRoleSchema.nullable().optional(),
  eventType: AssessmentEventTypeSchema,
  previousValue: z.unknown().nullable().optional(),
  newValue: z.unknown().nullable().optional(),
  trigger: z.string().nullable().optional(),
  timestamp: z.string().datetime(),
});
export type AssessmentEventDto = z.infer<typeof AssessmentEventSchema>;

// ============================================================================
// INPUT SCHEMAS
// ============================================================================

/**
 * Input for creating a new digital twin
 */
export const CreateDigitalTwinInputSchema = z.object({
  businessId: z.string().uuid(),
  role: TwinRoleSchema,
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  status: TwinStatusSchema.optional().default(TwinStatus.ACTIVE),
  config: z.record(z.string(), z.unknown()).nullable().optional(),
  triggerEvents: z.array(z.string()).optional(),
  promptTemplateId: z.string().uuid().nullable().optional(),
  modelConfigId: z.string().uuid().nullable().optional(),
  workflowId: z.string().uuid().nullable().optional(),
});
export type CreateDigitalTwinInput = z.infer<typeof CreateDigitalTwinInputSchema>;

/**
 * Input for updating an existing digital twin
 */
export const UpdateDigitalTwinInputSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
  status: TwinStatusSchema.optional(),
  config: z.record(z.string(), z.unknown()).nullable().optional(),
  triggerEvents: z.array(z.string()).optional(),
  promptTemplateId: z.string().uuid().nullable().optional(),
  modelConfigId: z.string().uuid().nullable().optional(),
  workflowId: z.string().uuid().nullable().optional(),
});
export type UpdateDigitalTwinInput = z.infer<typeof UpdateDigitalTwinInputSchema>;

/**
 * Input for overriding an assessment score or recommendation
 */
export const OverrideAssessmentInputSchema = z.object({
  assessmentId: z.string().uuid(),
  overallScore: z.number().min(0).max(100).optional(),
  riskBand: RiskBandSchema.optional(),
  recommendation: AssessmentRecommendation.optional(),
  reason: z.string().min(1).max(2000),
});
export type OverrideAssessmentInput = z.infer<typeof OverrideAssessmentInputSchema>;

/**
 * Input for triggering a credit pull
 */
export const TriggerCreditPullInputSchema = z.object({
  caseId: z.string().uuid(),
  consumerId: z.string().uuid(),
  bureau: CreditBureauSchema,
  requestType: z.enum(['SOFT', 'HARD']).optional().default('SOFT'),
});
export type TriggerCreditPullInput = z.infer<typeof TriggerCreditPullInputSchema>;

// ============================================================================
// PROPERTY EVALUATION (Issue #1617)
// ============================================================================

/** Fix-and-flip strategy projection for a property deal. */
export interface PropertyEvaluationStrategyFlip {
  /** Return on investment, expressed as a fraction in 0.0-1.0. */
  ROI: number;
  /** Projected dollar profit at the end of the flip cycle. */
  profit: number;
  /** Expected hold period before resale, in months. */
  holdMonths: number;
  /** Maximum allowable offer in dollars to hit target return. */
  MAO: number;
}

/**
 * Buy-and-hold rental strategy projection for a property deal.
 *
 * All metric fields are nullable: when the borrower has not provided an
 * `expectedRent`, the deterministic math cannot project NOI, cash flow, cap
 * rate, cash-on-cash, or DSCR. In that case the producer returns the
 * "all-null" shape so callers can render the empty state instead of a
 * misleading `0`.
 */
export interface PropertyEvaluationStrategyRentalHold {
  /** Net monthly cash flow in dollars after debt service + expenses. */
  cashFlowMonthly: number | null;
  /** Capitalisation rate (NOI / value). */
  capRate: number | null;
  /** Cash-on-cash return (annual cash flow / cash invested). */
  cashOnCash: number | null;
  /** Debt-service coverage ratio. */
  DSCR: number | null;
  /** Maximum purchase price in dollars to hit DSCR/cap-rate targets. */
  maxPurchase: number | null;
}

/** Single rehab line item from vision/text scope detection or manual entry. */
export interface PropertyEvaluationRehabLineItem {
  /**
   * Rehab category. Free-form string but conventionally one of:
   * 'General' | 'Paint/Drywall' | 'Flooring' | 'Kitchen' | 'Bathroom' |
   * 'HVAC' | 'Exterior'.
   */
  category: string;
  /** Free-form description of the work scope. */
  scope: string;
  /** Low-end cost estimate in dollars. */
  low: number;
  /** Mid-point cost estimate in dollars. */
  mid: number;
  /** High-end cost estimate in dollars. */
  high: number;
  /** Origin of this line item. */
  source: 'AI_VISION' | 'AI_TEXT' | 'MANUAL_OVERRIDE';
}

/** Underwriter-facing risk flag bucketed by recommended action. */
export interface PropertyEvaluationRiskFlag {
  id: string;
  /**
   * Recommended bucket for the underwriter:
   * - `resolve`   — must clear before committing
   * - `negotiate` — price into the offer or get a credit
   * - `accept`    — disclose, no action required
   */
  bucket: 'resolve' | 'negotiate' | 'accept';
  title: string;
  /** Short impact statement (e.g. "+$8K to rehab budget"). Free-form, rendered verbatim by the UI. */
  impact: string;
  /**
   * Machine-readable severity for filtering/sorting in the UI. The Gemini
   * risk-flags prompt asks the model to emit this alongside the free-form
   * `impact` copy. Phase 2 (#1623 PR review): added to separate severity
   * (sort key) from impact (display text). Optional for backward-compat
   * with rows persisted before this field existed.
   */
  severity?: PropertyRiskFlagSeverity;
  details: string;
  recommendedAction: string;
  /** Optional task id for a linked workflow task. */
  linkedTaskId?: string;
}

/**
 * Single per-feature adjustment row in the comp's reconciliation table
 * (e.g. "GLA (sqft) | 1,568 | 1,612 | -$6,300"). Mirrors the FNM 1004
 * adjustment grid — feature label + subject value + comp value + signed
 * dollar adjustment. The string form on `subjectValue`/`compValue` keeps
 * the wire shape flexible (e.g. dates, condition codes, dashes for
 * "base price" rows).
 */
export interface PropertyCompAdjustmentRow {
  feature: string;
  subjectValue: string;
  compValue: string;
  /** Signed dollar adjustment, or null for the "Sale price · base" row. */
  adjustmentUsd: number | null;
}

/** Single comparable property record (sale or rent). */
export interface PropertyComp {
  address: string;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  /** Closed sale price or asking rent in dollars. */
  price: number | null;
  /** ISO 8601 closed-at date, or null for active listings. */
  closedAt: string | null;
  /** Distance from subject property in miles. */
  distanceMi: number | null;
  // ==========================================================================
  // Model 2 reconciliation fields — populated by the comp-adjustment
  // heuristic. Optional so legacy rows that don't carry adjustments
  // continue to project cleanly through `PropertyEvaluationFull`.
  // ==========================================================================
  /** UAD condition code (C1–C6). */
  conditionCode?: 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6' | null;
  /** UAD quality code (Q1–Q6). */
  qualityCode?: 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Q5' | 'Q6' | null;
  /** Year built (subject reconciliation). */
  yearBuilt?: number | null;
  /** Days on market for the comp (when known). */
  domDays?: number | null;
  /** Per-feature adjustment rows for the expandable detail table. */
  adjustments?: PropertyCompAdjustmentRow[] | null;
  /** Sum of all adjustments applied to the base sale price. */
  adjustedSalePrice?: number | null;
  /** Reconciliation weight (0..1). UI renders as a percentage bar. */
  weight?: number | null;
  /** Provenance of the comp record (e.g. "Class Data", "RentCast"). */
  source?: string | null;
  /** SHAP-style top driver — the feature with the largest |adjustment|. */
  shapTopDriver?: string | null;
  /** Whether all adjustments stayed within FNM 1004 caps. */
  withinFnm1004Caps?: boolean | null;
}

/** Snapshot of market conditions for the subject property's ZIP. */
export interface PropertyMarketSnapshot {
  zip: string;
  medianSalePrice: number | null;
  medianDaysOnMarket: number | null;
  /** Six-month price change as a fraction (e.g. 0.04 = +4%). */
  sixMonthPriceChangePct: number | null;
  activeListings: number | null;
}

/**
 * Subject-property facts surfaced under the page title in the AVM/ARV
 * report layout (PDF spec, page 1). Sourced opportunistically from the
 * SFR Analytics property record + case metadata; nullable end-to-end so
 * the report header can degrade to "—" when a fact is missing.
 */
export interface PropertySubjectFacts {
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  yearBuilt: number | null;
  /** Lot size in acres. */
  lotSizeAcres: number | null;
  /** Free-form property type label (e.g. "Single-family detached"). */
  propertyTypeLabel: string | null;
  /** Neighborhood/subdivision name when known (e.g. "Camelot II"). */
  neighborhood: string | null;
}

/**
 * UAD (FNM 1004 / FHLMC) condition + quality ratings — Model 1 of the
 * AVM report. C1-C6 and Q1-Q6 are the canonical 6-step UAD scales:
 *   C1 New / never occupied  · Q1 Luxury, custom
 *   C2 No deferred maint.   · Q2 High-end finishes
 *   C3 Well maintained      · Q3 Above-average finishes
 *   C4 Adequate, some wear  · Q4 Standard builder-grade
 *   C5 Obvious deferred     · Q5 Low-end, economy
 *   C6 Substantial repair   · Q6 Substandard
 *
 * Subject = current state. Ceiling = highest C/Q the neighborhood
 * supports (derived from comp distribution). Recommended = ROI-optimal
 * renovation target on the marginal-value curve (often below ceiling).
 */
export type UadConditionRating = 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';
export type UadQualityRating = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Q5' | 'Q6';

export interface PropertyUadRating {
  subjectCondition: UadConditionRating | null;
  subjectQuality: UadQualityRating | null;
  ceilingCondition: UadConditionRating | null;
  ceilingQuality: UadQualityRating | null;
  recommendedCondition: UadConditionRating | null;
  recommendedQuality: UadQualityRating | null;
  /** Confidence in the rating, 0.0-1.0. */
  modelConfidence: number | null;
}

/**
 * Per-deal ROI breakdown line items, surfaced in the "ROI breakdown"
 * card on page 3 of the AVM report. All dollar values; nullable when
 * the upstream input is absent so the UI can render "—" cleanly.
 */
export interface PropertyRoiBreakdown {
  arvAtTarget: number | null;
  /** Acquisition cost — typically purchase price OR as-is fair value. */
  acquisition: number | null;
  /** Rehab cost at the recommended renovation target. */
  rehabCost: number | null;
  /** Holding cost over the projected hold window (default 60 days). */
  holdingCost: number | null;
  /** Selling cost — agent + closing, typically 6-7% of ARV. */
  sellingCost: number | null;
  /** Net profit when acquired at fair value (= ARV - sum of costs). */
  netProfitAtFairValue: number | null;
  /**
   * Net profit when acquired at a target discount below as-is value.
   * The PDF shows "If acquired at 10% below as-is" — the discount
   * percentage is captured here so the UI can label the row.
   */
  netProfitAtDiscount: number | null;
  /** Discount fraction the netProfitAtDiscount row assumes (e.g. 0.10). */
  acquisitionDiscountPct: number | null;
}

/**
 * Days-on-market analysis — Model 5 in the AVM report. Combines a
 * recommended list price with percentile-based DOM expectations and a
 * cumulative-probability curve for the survival chart.
 */
export interface PropertyDomAnalysis {
  /** Recommended list price in dollars (typically ARV ± fairness offset). */
  recommendedListPrice: number | null;
  /** Median expected days on market at the recommended list price. */
  expectedDomP50: number | null;
  expectedDomP25: number | null;
  expectedDomP75: number | null;
  expectedDomP90: number | null;
  /**
   * Cumulative probability of sale at canonical day milestones — drives
   * the "by day 15 / 30 / 45 / 60" tile row on page 3.
   */
  byDayProbability: Array<{ days: number; probability: number }>;
  /**
   * Price-vs-DOM tradeoff curve points — drives the line chart with the
   * recommended-list marker and the velocity-collapse danger zone. Each
   * point: list price ($k) + expected DOM (days). The point flagged
   * `isRecommended=true` renders as the "you are here" marker.
   */
  priceVsDomCurve?: Array<{ priceK: number; domDays: number; isRecommended?: boolean }>;
  /**
   * Price threshold above which expected DOM crosses into the "velocity
   * collapse" zone. The chart shades this region in the rose tint.
   */
  velocityCollapsePriceK?: number | null;
}

/**
 * Single renovation scenario in the marginal-value curve (Model 3 +
 * Ceiling Classifier output). The full curve is 4 scenarios from
 * "condition fix only" to "overbuild," with one flagged as the ROI
 * peak (target) and one as the neighborhood ceiling.
 */
export interface PropertyRenovationScenario {
  /** Display label, e.g. "C3/Q3" or "C3/Q2". */
  label: string;
  /** Short scope description, e.g. "condition fix" or "kitchen + bath". */
  scope: string;
  /** Dollar value the scenario adds vs. as-is. */
  valueAdded: number | null;
  /** Rehab cost for this scenario. */
  rehabCost: number | null;
  /** Net ROI (valueAdded - rehabCost). */
  netRoi: number | null;
  /** True for the ROI-optimal target. */
  isTarget?: boolean;
  /** True for the neighborhood ceiling (highest C/Q supported). */
  isCeiling?: boolean;
  /** True for the overbuild scenario (negative marginal return). */
  isOverbuild?: boolean;
}

/**
 * Acquisition recommendation — Accept/Reject + max-acquisition price
 * + inline ROI breakdown. Drives the "Recommendation" banner at the
 * top of the Risk card.
 */
export interface PropertyRecommendation {
  /** Verdict bucket. */
  verdict: 'ACCEPT' | 'NEGOTIATE' | 'REJECT';
  /** Maximum acquisition price for the deal to remain positive (dollars). */
  maxAcquisitionPrice: number | null;
  /** One-line summary for the banner subtitle. */
  reasoning: string;
}

/**
 * Extended market metrics — adds the Sale-to-list ratio, Months of
 * supply, and a histogram of recent sale prices for the price-distribution
 * card. Layered on top of `PropertyMarketSnapshot`; rendering surfaces
 * still consume the base snapshot for the headline cells.
 */
export interface PropertyMarketExtras {
  /** Sale-to-list ratio for the ZIP (0..1+, e.g. 0.982 = 98.2%). */
  saleToListRatio: number | null;
  /** Months of supply at current absorption pace. */
  monthsOfSupply: number | null;
  /**
   * Histogram of recent sale prices in $40k bins. Each bin: lower bound
   * in $k + count of sales. The chart marks the bin containing the
   * subject ARV.
   */
  priceDistributionBins: Array<{ lowerK: number; count: number }>;
  /** ZIP-bin lower bound containing the subject ARV. */
  subjectBinLowerK: number | null;
}

/**
 * Underwriter projection of a property evaluation — the full record.
 * Phase 2 borrower/partner views project a redacted subset of this.
 */
export interface PropertyEvaluationFull {
  id: string;
  caseId: string;
  businessId: string;
  propertyAddress: string;
  propertyType: string;
  /**
   * Borrower-stated purchase price in dollars. Sourced from the underlying
   * `PropertyValuation.purchasePrice` Float column — non-null in the DB but
   * projected as `number | null` so empty/missing rows surface as the
   * SnapshotSection empty marker rather than rendering `0` as a real price.
   */
  purchasePrice: number | null;
  verdict: PropertyVerdict | null;
  /** Confidence in the verdict, 0.0-1.0. */
  confidence: number | null;
  runDepth: PropertyRunDepth;
  source: PropertyValuationSource;
  /** ISO 8601 valuation timestamp. */
  valuationDate: string;
  asIsValue: number | null;
  asIsValueP10: number | null;
  asIsValueP90: number | null;
  /** After-repair value (dollars). */
  arv: number | null;
  /** Optional executive-summary narrative (Phase 2). */
  narrative: string | null;
  strategies: {
    flip?: PropertyEvaluationStrategyFlip;
    rentalHold?: PropertyEvaluationStrategyRentalHold;
  } | null;
  rehabLineItems: PropertyEvaluationRehabLineItem[] | null;
  riskFlags: {
    resolve: PropertyEvaluationRiskFlag[];
    negotiate: PropertyEvaluationRiskFlag[];
    accept: PropertyEvaluationRiskFlag[];
  } | null;
  comparables: { sale: PropertyComp[]; rent: PropertyComp[] };
  marketSnapshot: PropertyMarketSnapshot | null;
  /**
   * Property-category factors driving the verdict. Uses the existing
   * AssessmentFactorDto shape so consumers can render alongside other
   * assessment factors without a separate view model.
   */
  factors: AssessmentFactorDto[];
  /** ISO 8601 created-at timestamp. */
  createdAt: string;
  // ==========================================================================
  // AVM/ARV report extensions (PDF design vision — page 1-3 enrichments).
  // All fields are optional so persisted records that pre-date this work
  // surface as `null`/`undefined` and let the UI render the empty-state
  // demote without a migration. Heuristic computation lives in
  // `PropertyEvaluatorService` — the values here are derived, not stored
  // separately on PropertyValuation.
  // ==========================================================================
  /** Subject-property facts row (header sub-line on the report). */
  subjectFacts?: PropertySubjectFacts | null;
  /** UAD condition + quality ratings (Model 1). */
  uadRating?: PropertyUadRating | null;
  /** Per-deal ROI breakdown line items (Model + Ceiling Classifier). */
  roiBreakdown?: PropertyRoiBreakdown | null;
  /** DOM/survival analysis at the recommended list price (Model 5). */
  domAnalysis?: PropertyDomAnalysis | null;
  /**
   * 4-step renovation scenarios for the marginal-value curve chart.
   * One of the scenarios should have `isTarget=true` (ROI peak); one
   * `isCeiling=true` (neighborhood ceiling).
   */
  renovationScenarios?: PropertyRenovationScenario[] | null;
  /** Acquisition recommendation banner for the Risk card. */
  recommendation?: PropertyRecommendation | null;
  /** Extended market metrics (Sale/list ratio, supply, histogram). */
  marketExtras?: PropertyMarketExtras | null;
  /**
   * Report metadata — generated identifier + last-run timestamp display
   * fields for the Snapshot bar header. The id is a deterministic
   * derivative of `(caseId, valuationDate)` so the same triage row
   * always surfaces the same VAI-* string.
   */
  reportId?: string | null;
}

/**
 * Phase 2 borrower projection of a property evaluation. Carries no
 * internal scoring fields (verdict, confidence, factors, narrative).
 */
export interface PropertyEvaluationBorrowerView {
  caseId: string;
  propertyAddress: string;
  /** Borrower-stated purchase price in dollars (mirrors `PropertyEvaluationFull.purchasePrice`). */
  purchasePrice: number | null;
  asIsValue: number | null;
  asIsValueP10: number | null;
  asIsValueP90: number | null;
  comparables: { sale: PropertyComp[]; rent: PropertyComp[] };
  marketSnapshot: PropertyMarketSnapshot | null;
  /** Action items the borrower should address — risk flags only, no scoring. */
  itemsToAddress: PropertyEvaluationRiskFlag[];
}

/**
 * Phase 2 partner projection of a property evaluation. Currently identical
 * to the borrower view — partners and borrowers see the same redacted
 * record. Kept as a separate exported alias so the projection can diverge
 * without breaking downstream consumers.
 */
export type PropertyEvaluationPartnerView = PropertyEvaluationBorrowerView;

/**
 * Input for `PropertyEvaluatorService.runFullMemo` (Phase 2 #1623). Drives
 * the full Gemini-backed property memo: rehab scope + risk flags + narrative
 * + flip / rental-hold strategy math. Idempotency is keyed off
 * `(businessId, caseId, twinId)` unless `force` is set.
 */
export interface RunFullMemoInput {
  businessId: string;
  caseId: string;
  twinId: string;
  /** Optional override of buybox / hold months / financing assumptions. */
  options?: Record<string, unknown>;
  /** Re-run even if idempotency cache shows a recent FULL_MEMO. */
  force?: boolean;
}
